/**
 * What this build is.
 *
 * Both values are stamped in at build time (see the `define` block in the web
 * and desktop vite configs). They are read defensively because the same UI is
 * also loaded by tests and tooling that do no such substitution.
 *
 * The version is what the update check compares against the server, and the
 * build date is what tells a stale install apart from a fresh one — which is
 * otherwise invisible and has cost real time chasing already-fixed bugs.
 */

export function appVersion () {
  try {
    return typeof __PINE_VERSION__ === 'string' ? __PINE_VERSION__ : '0.0.0'
  } catch {
    return '0.0.0'
  }
}

export function buildDate () {
  try {
    return typeof __PINE_BUILD__ === 'string' ? __PINE_BUILD__ : ''
  } catch {
    return ''
  }
}

/**
 * Compare two dotted versions. Returns > 0 when `a` is newer than `b`.
 * Anything unparseable sorts as 0 so a malformed answer never claims an update.
 */
export function compareVersions (a, b) {
  const parse = (v) => String(v || '').split('.').map((n) => parseInt(n, 10) || 0)
  const left = parse(a)
  const right = parse(b)
  for (let i = 0; i < Math.max(left.length, right.length); i++) {
    const d = (left[i] || 0) - (right[i] || 0)
    if (d !== 0) return d
  }
  return 0
}

/**
 * Ask the server which version the download page is serving.
 *
 * Returns null when there is no registry configured, the server is old enough
 * not to answer, or the network is down — all of which mean "do not claim an
 * update is available".
 */
export async function fetchLatestVersion (registryUrl) {
  const base = String(registryUrl || '').replace(/\/+$/, '')
  if (!base) return null
  try {
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), 6000)
    const res = await fetch(base + '/api/version', { signal: controller.signal })
    clearTimeout(timer)
    if (!res.ok) return null
    const body = await res.json()
    return body && typeof body.version === 'string'
      ? { version: body.version, downloads: base + (body.downloads || '/install.html') }
      : null
  } catch {
    return null
  }
}
